import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Calendar, MapPin, Building2, Sparkles, X, Loader2, ShieldCheck, MessageCircle, Target as TargetIcon, Info } from "lucide-react";
import { Loader2 as Spinner } from "lucide-react";
import { api, waUrl, WA_MESSAGES } from "../lib/api";
import { clientSideFallback, isValidWhatsApp } from "../lib/fallbackRecommendations";
import { toast } from "sonner";

const STEPS = [
    { key: "current_education", label: "Tingkat pendidikan saat ini", options: ["SMA/SMK Kelas 11", "SMA/SMK Kelas 12", "Mahasiswa S1 Aktif", "Lulusan S1", "Lulusan S2", "Orang Tua Murid"] },
    { key: "target_degree", label: "Target jenjang studi", options: ["Foundation / Pathway", "Diploma", "S1", "S2", "S3", "Short Course / Professional Program"] },
    { key: "target_country", label: "Negara tujuan utama", options: ["Australia", "United Kingdom", "Malaysia", "Singapore", "Germany", "Netherlands", "Canada", "United States", "Belum yakin"] },
    { key: "budget_idr", label: "Perkiraan budget keluarga per tahun", options: ["Di bawah Rp150 juta", "Rp150–300 juta", "Rp300–500 juta", "Rp500 juta–Rp1 miliar", "Di atas Rp1 miliar", "Masih ingin dihitung dulu"] },
];

const BUDGET_MAP = {
    "Di bawah Rp150 juta": 120_000_000,
    "Rp150–300 juta": 220_000_000,
    "Rp300–500 juta": 400_000_000,
    "Rp500 juta–Rp1 miliar": 750_000_000,
    "Di atas Rp1 miliar": 1_500_000_000,
    "Masih ingin dihitung dulu": 300_000_000,
};

const CATEGORIES = ["Safe", "Match", "Ambitious"];

export default function SmartSearch() {
    const [step, setStep] = useState(0);
    const [data, setData] = useState({});
    const [contactStep, setContactStep] = useState(false);
    const [contact, setContact] = useState({ name: "", whatsapp: "", email: "" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const select = (val) => {
        const key = STEPS[step].key;
        const finalVal = key === "budget_idr" ? BUDGET_MAP[val] : val;
        const updated = { ...data, [key]: finalVal, [`${key}_label`]: val };
        setData(updated);
        if (step < STEPS.length - 1) {
            setTimeout(() => setStep(step + 1), 200);
        } else {
            setTimeout(() => setContactStep(true), 200);
        }
    };

    const submit = async () => {
        if (!contact.name.trim()) {
            toast.error("Mohon isi nama dulu ya, Kak");
            return;
        }
        if (!isValidWhatsApp(contact.whatsapp)) {
            toast.error("Format WhatsApp belum valid. Contoh: 081234567890 atau 6281234567890");
            return;
        }
        setLoading(true);

        const payload = {
            current_education: data.current_education,
            target_degree: data.target_degree,
            major: data.target_country,
            budget_idr: data.budget_idr,
            name: contact.name,
            email: contact.email || null,
            whatsapp: contact.whatsapp,
        };

        // Always have a deterministic fallback ready — user must never reach dead end
        const fallback = clientSideFallback({
            current_education: data.current_education,
            target_degree: data.target_degree,
            target_country: data.target_country_label || data.target_country,
            budget_idr: data.budget_idr,
        });

        try {
            const res = await api.post("/ai/recommend", payload, { timeout: 30000 });
            const apiResult = res.data;
            // Merge API result with fallback fields (budget_note, readiness_note, next_step, disclaimer)
            // because backend may not provide all of them
            setResult({
                ...fallback,
                ...apiResult,
                budget_note: apiResult.budget_note || fallback.budget_note,
                readiness_note: apiResult.readiness_note || fallback.readiness_note,
                next_step: apiResult.next_step || fallback.next_step,
                disclaimer: apiResult.is_client_fallback ? fallback.disclaimer : null,
                is_client_fallback: false,
            });
        } catch (e) {
            // API failed (network, timeout, 5xx, CORS, anything). Show client fallback.
            console.warn("[SmartSearch] API failed, using client-side fallback:", e?.message || e);
            setResult(fallback);
            // Fire-and-forget: try to capture lead anyway via /leads endpoint
            api.post("/leads", {
                name: contact.name,
                email: contact.email || null,
                whatsapp: contact.whatsapp,
                source: "smartsearch_client_fallback",
                payload: {
                    "Pendidikan saat ini": data.current_education,
                    "Target gelar": data.target_degree,
                    "Negara tujuan": data.target_country_label || data.target_country,
                    "Budget per tahun (IDR)": data.budget_idr,
                    "Budget per tahun": `Rp ${Number(data.budget_idr).toLocaleString("id-ID")}`,
                    "Mode": "Client-side fallback (API unreachable)",
                },
            }).catch((err) => console.warn("[SmartSearch] Lead capture also failed:", err?.message));
            toast.info("Rekomendasi awal sudah siap. Counselor Gantari akan bantu validasi lebih detail via WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setStep(0);
        setData({});
        setContact({ name: "", whatsapp: "", email: "" });
        setContactStep(false);
        setResult(null);
    };

    return (
        <section id="smart-search" className="bg-[#F8FAFC] py-20 md:py-28" data-testid="smart-search-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12 max-w-2xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A1A2F] text-white text-xs font-semibold mb-4">
                        <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
                        LANGKAH 1 · GRATIS, TANPA DAFTAR
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1A2F] tracking-tight leading-tight">
                        Cari Kampus yang Cocok untuk Profilmu
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed">
                        Jawab beberapa pertanyaan singkat. Kami akan bantu memetakan pilihan negara, jurusan, dan kampus yang lebih realistis untukmu.
                    </p>
                </div>

                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    {!result && !loading && !contactStep && (
                        <div className="p-8 md:p-12">
                            <div className="flex items-center gap-2 mb-10">
                                {STEPS.map((s, i) => (
                                    <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-[#F5A623]" : "bg-slate-200"}`} data-testid={`step-indicator-${i}`} />
                                ))}
                                <div className={`h-1.5 flex-1 rounded-full transition-all bg-slate-200`} />
                            </div>
                            <AnimatePresence mode="wait">
                                <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
                                    <div className="text-xs uppercase tracking-[0.2em] text-[#F5A623] font-bold mb-3">Pertanyaan {step + 1} dari {STEPS.length}</div>
                                    <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0A1A2F] mb-8">{STEPS[step].label}</h3>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {STEPS[step].options.map((opt) => (
                                            <button
                                                key={opt}
                                                data-testid={`option-${STEPS[step].key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                                onClick={() => select(opt)}
                                                className="text-left border-2 border-slate-200 hover:border-[#0A1A2F] hover:bg-[#0A1A2F] hover:text-white rounded-2xl px-6 py-5 transition-all group flex items-center justify-between"
                                            >
                                                <span className="font-medium">{opt}</span>
                                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition" />
                                            </button>
                                        ))}
                                    </div>
                                    {step > 0 && (
                                        <button onClick={() => setStep(step - 1)} data-testid="step-back-btn" className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A1A2F]">
                                            <ArrowLeft className="w-4 h-4" /> Kembali
                                        </button>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    )}

                    {!result && !loading && contactStep && (
                        <div className="p-8 md:p-12">
                            <div className="flex items-center gap-2 mb-10">
                                {STEPS.map((s) => <div key={s.key} className="h-1.5 flex-1 rounded-full bg-[#F5A623]" />)}
                                <div className="h-1.5 flex-1 rounded-full bg-[#F5A623]" />
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] text-[#F5A623] font-bold mb-3">Langkah Terakhir</div>
                            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0A1A2F] mb-2">Hampir selesai!</h3>
                            <p className="text-sm text-slate-600 mb-7">Tinggal info kontak Kakak agar kami bisa kirim rekomendasi dan menghubungi untuk konsultasi awal.</p>
                            <div className="grid sm:grid-cols-3 gap-3">
                                <Field label="Nama Lengkap *" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} tid="assess-name" />
                                <Field label="WhatsApp *" value={contact.whatsapp} onChange={(v) => setContact({ ...contact, whatsapp: v })} tid="assess-whatsapp" />
                                <Field label="Email (opsional)" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} type="email" tid="assess-email" />
                            </div>
                            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                                <button onClick={() => setContactStep(false)} data-testid="contact-back-btn" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A1A2F]">
                                    <ArrowLeft className="w-4 h-4" /> Ubah jawaban
                                </button>
                                <button
                                    onClick={submit}
                                    data-cta="cta_assessment_submit"
                                    data-testid="cta-assessment-submit"
                                    className="ml-auto inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full px-7 py-3.5 font-bold transition-all"
                                >
                                    Lihat Rekomendasi Awal <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="mt-4 text-xs text-slate-500">Data Kakak hanya digunakan untuk konsultasi Gantari Edu — tidak akan dibagikan ke pihak lain.</p>
                        </div>
                    )}

                    {loading && (
                        <div className="p-16 text-center" data-testid="search-loading">
                            <Spinner className="w-10 h-10 mx-auto animate-spin text-[#F5A623]" />
                            <p className="mt-4 text-slate-600 font-medium">Gantari sedang menganalisa pilihan terbaik untuk Kakak...</p>
                            <p className="mt-1 text-xs text-slate-400">Mohon tunggu sebentar — proses biasanya 10-20 detik.</p>
                        </div>
                    )}

                    {result && (
                        <div className="p-8 md:p-12" data-testid="search-result">
                            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                                <div>
                                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F5A623] font-bold mb-3 px-2.5 py-1 rounded-full bg-[#F5A623]/15">
                                        <Sparkles className="w-3 h-3" /> Assessment Awal Selesai
                                    </div>
                                    <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0A1A2F]">Rekomendasi Awalmu Sudah Siap</h3>
                                    <p className="text-sm text-slate-500 mt-2 max-w-2xl">
                                        Kami sudah memetakan pilihan awal berdasarkan jawabanmu. Selanjutnya, Counselor Senior Gantari Edu akan membantu validasi kampus, jurusan, biaya, dokumen, dan timeline aplikasimu.
                                    </p>
                                </div>
                                <button onClick={reset} className="text-sm text-slate-500 hover:text-[#0A1A2F]" data-testid="reset-search-btn">Ubah Jawaban</button>
                            </div>

                            {result.ringkasan && (
                                <p className="text-slate-700 mb-6 bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-2xl px-5 py-4" data-testid="result-ringkasan">{result.ringkasan}</p>
                            )}

                            {result.is_client_fallback && (
                                <div className="mb-6 flex items-start gap-2.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-4">
                                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <p>Sistem validasi otomatis kami sedang sibuk. Rekomendasi awal di bawah ini sudah kami siapkan dengan logika deterministik — Counselor Gantari akan bantu cek lebih detail via WhatsApp.</p>
                                </div>
                            )}

                            <div className="grid md:grid-cols-3 gap-5">
                                {(result.rekomendasi || []).slice(0, 3).map((r, i) => (
                                    <motion.div
                                        key={r.negara || `rec-${i}`}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        className="border border-slate-200 rounded-2xl p-6 hover:border-[#0A1A2F] hover:shadow-lg transition-all"
                                        data-testid={`rec-card-${i}`}
                                    >
                                        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-3 px-2.5 py-1 rounded-full ${
                                            i === 0 ? "bg-emerald-100 text-emerald-700" : i === 1 ? "bg-[#F5A623]/15 text-[#D98F1B]" : "bg-red-100 text-red-600"
                                        }`}>
                                            <TargetIcon className="w-3 h-3" /> {CATEGORIES[i] || "Option"} Options
                                        </div>
                                        <h4 className="font-display text-2xl font-bold text-[#0A1A2F]">{r.negara}</h4>
                                        <p className="text-sm text-slate-600 mt-2 line-clamp-4">{r.alasan}</p>
                                        {r.estimasi_biaya_tahunan_idr && (
                                            <>
                                                <div className="mt-4 text-xs text-slate-500">Estimasi biaya/tahun</div>
                                                <div className="font-display font-bold text-lg text-[#0A1A2F]">Rp {Number(r.estimasi_biaya_tahunan_idr).toLocaleString("id-ID")}</div>
                                            </>
                                        )}
                                        <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
                                            {(r.universitas || []).map((u, ui) => (
                                                <div key={u.nama || `uni-${ui}`} className="flex items-start gap-2">
                                                    <Building2 className="w-4 h-4 text-[#F5A623] mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <div className="text-sm font-semibold text-[#0A1A2F]">{u.nama}</div>
                                                        <div className="text-xs text-slate-500">{u.kota} · {u.highlight}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {result.budget_note && (
                                <div className="mt-6 bg-slate-50 border border-slate-200 rounded-2xl p-5" data-testid="result-budget-note">
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-[#F5A623] mb-1.5">Catatan Budget</div>
                                    <p className="text-sm text-slate-700">{result.budget_note}</p>
                                </div>
                            )}

                            {result.readiness_note && (
                                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl p-5" data-testid="result-readiness-note">
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-[#F5A623] mb-1.5">Kesiapan Aplikasi</div>
                                    <p className="text-sm text-slate-700">{result.readiness_note}</p>
                                </div>
                            )}

                            {result.next_step && (
                                <div className="mt-3 bg-[#0A1A2F]/5 border border-[#0A1A2F]/20 rounded-2xl p-5" data-testid="result-next-step">
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-[#0A1A2F] mb-1.5">Langkah Selanjutnya</div>
                                    <p className="text-sm text-slate-700">{result.next_step}</p>
                                </div>
                            )}

                            <div className="mt-6 flex items-start gap-2.5 text-xs text-slate-500 bg-slate-50 rounded-xl p-4">
                                <ShieldCheck className="w-4 h-4 text-[#F5A623] flex-shrink-0 mt-0.5" />
                                <p>
                                    {result.disclaimer || "Hasil ini adalah pemetaan awal."} <strong className="text-[#0A1A2F]">Counselor Senior Gantari Edu</strong> akan membantu validasi pilihanmu secara personal.
                                    <span className="block mt-1 text-slate-400">Data Kakak hanya digunakan untuk konsultasi Gantari Edu dan tidak akan dibagikan ke pihak lain tanpa persetujuan.</span>
                                </p>
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 bg-[#0A1A2F] rounded-2xl p-6 sm:p-8">
                                <div className="flex-1 text-center sm:text-left">
                                    <div className="font-display font-bold text-white text-lg">Mau diskusi lebih dalam, Kak?</div>
                                    <div className="text-sm text-slate-300">Konsultasi gratis dengan Counselor Senior via WhatsApp.</div>
                                </div>
                                <a
                                    href={waUrl(WA_MESSAGES.assessment)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-cta="cta_assessment_whatsapp"
                                    data-testid="assess-cta-whatsapp"
                                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB454] text-white rounded-full px-6 py-3 font-bold transition-all whitespace-nowrap"
                                >
                                    <MessageCircle className="w-4 h-4" /> Konsultasi Gratis via WhatsApp
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function Field({ label, value, onChange, type = "text", tid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</label>
            <input
                type={type} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={tid}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-[#0A1A2F] outline-none transition text-sm"
            />
        </div>
    );
}
